import { SelectItemAdditionalParams } from '../../hooks/bi/useTrackItemSelectAnalytics/types';
import { MediaManagerSDKAnyItem, MediaManagerSDKFolder } from '../../media-manager-sdk';
import { RelatedItemState } from '../withRelatedItems/withRelatedItems.types';

export enum JumpTo {
  Next,
  Prev,
  Bottom,
  Top,
}

export type TEntities = {
  items?: { [key: string]: MediaManagerSDKAnyItem };
  relatedItems?: { [key: string]: MediaManagerSDKAnyItem };
};

export enum SelectedItemType {
  Item,
  RelatedItem,
}

export type SelectedItemTypes = Record<string, SelectedItemType>;

export interface IItemsReducer {
  items: MediaManagerSDKAnyItem[];
  selectedItemTypes: SelectedItemTypes;
  relatedItems: MediaManagerSDKAnyItem[];
  isRelatedItemsLoading: boolean;
  isLoading: boolean;
  selectedItems: MediaManagerSDKAnyItem[];
  selectedItemsCount: number;
  editingItem: MediaManagerSDKAnyItem;
  hasSelectedItems: boolean;
  isSingleItemSelected: boolean;
  hasNoItems: boolean;
}

export type TState = {
  entities: TEntities;
  items: string[];
  relatedItems: string[];
  isLoading: boolean;
  selectedItems: string[];
  selectedItemTypes: Record<string, SelectedItemType>;
  lastSelectedItems: string[];
  lastSelectedItemId: string;
  lastSelectedItemType: SelectedItemType;
  editingItem: MediaManagerSDKAnyItem;
  newFolderId: string;
};

export enum ItemsAction {
  Reset = 'reset',
  SetRelatedItems = 'set-related-items',

  AppendItems = 'append-items',
  PrependItems = 'prepend-items',
  InsertAfterFolders = 'insert-after-folders',
  RemoveItems = 'remove-items',

  ClearAll = 'clear-all',
  ClearItemsSelection = 'clear-items-selection',
  ClearRelatedItemsSelection = 'clear-related-items-selection',

  SelectItems = 'select-items',
  SelectAllItems = 'select-all-items',
  SelectItemsUntil = 'select-items-until',

  EditItemCancel = 'edit-item-cancel',
  EditItemStart = 'edit-item-start',
  EditItemComplete = 'edit-item-complete',
  UpdateEditingItem = 'update-editing-item',

  UpdateLocalItem = 'update-local-item',

  AddItemToSelection = 'add-item-to-selection',
  RemoveItemFromSelection = 'remove-item-from-selection',

  JumpToNextItem = 'jump-to-next-item',

  CreateNewFolderStart = 'create-new-folder-start',
  CreateNewFolderCancel = 'create-new-folder-cancel',
  CreateNewFolderComplete = 'create-new-folder-complete',
}

export type TUpdatedItem = {
  id?: string;
  name?: string;
  labels?: string[];
};

export type TItemsReducerAction = {
  type: ItemsAction;
  payload?: {
    itemId?: string;
    itemIds?: string[];
    newFolderId?: string;
    item?: MediaManagerSDKAnyItem;
    items?: MediaManagerSDKAnyItem[];
    relatedItems?: MediaManagerSDKAnyItem[];
    isLoading?: boolean;
    updatedItem?: TUpdatedItem;
    isInExpandedMode?: boolean;
    shouldSelectNextItem?: boolean;
    withShift?: boolean;
    jumpTo?: JumpTo;
    itemForPreview?: MediaManagerSDKAnyItem;
    isRelatedItem?: boolean;
    activeRelatedItemsState?: RelatedItemState;
  };
};

export interface IWithItemsReducerOuter {
  fileType: string;
  items: MediaManagerSDKAnyItem[];
  nextPageItems: MediaManagerSDKAnyItem[];
}

export type TToggleItemSelection = (itemId: string, params?: Partial<SelectItemAdditionalParams>) => void;

export interface IWithItemsReducerInner extends IItemsReducer {
  selectItem: (itemId: string, params?: object) => void;
  selectItems(itemIds: string[]): void;
  selectItemsUntil(itemId: string): void;
  prependItems: (items: MediaManagerSDKAnyItem[]) => void;
  removeItems(items: MediaManagerSDKAnyItem[]): void;
  updateLocalItem: (itemId: string, item: Partial<MediaManagerSDKAnyItem>) => void;
  updateEditingItem(item: Partial<MediaManagerSDKAnyItem>): void;
  clearItemsSelection(): void;
  clearRelatedItemsSelection(): void;
  createNewFolderStart(id: string): void;
  isItemSelected(id: string): boolean;
  isItemEditing(id: string): boolean;
  insertAfterFolders(items: MediaManagerSDKAnyItem[]): void;
  toggleItemSelection: TToggleItemSelection;
  createNewFolderComplete(folder: MediaManagerSDKFolder): void;
  createNewFolderCancel(): void;
  editItemComplete(): void;
}
